import {
  ReactNode,
  useCallback,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import TrashLineIcon from "remixicon-react/DeleteBin2LineIcon";
import UserLineIcon from "remixicon-react/UserLineIcon";

import { Text, InfoContainer, Table } from "$components";
import useCompany from "$hooks/useCompany";
import { theme } from "$utils/theme/bataillog";
import { ACTIVITIES_LABELS, getActivitiesOptions } from "$utils/typeConstants";

import { useActivitiesQuery } from "$hooks/useActivitiesQuery";
import { CompanyType } from "$types";
import UserForm from "../../Coordinators/UserForm";
import { usePopin } from "$components/Popin/usePopin";
import useUserForm from "$hooks/useUserForm";
import { User } from "$types/User";

import useAuth from "$hooks/useAuth";

import { DeleteUser } from "$services/Coordinators.service";
import { FormHeader } from "$components/Forms/FormHeader";
import { TitleInfoContainer } from "$components/InfoContainer/TitleInfoContainer";
import { useModal } from "$components/JModal/useModal";
import { SimpleMuiButton } from "$components/Buttons/MuiButton";

// TODO il faut déplacer cette partie dans un composant à part
// Il faut également retirer la dépendance avec le composant contact
function TestForm({
  user,
  title,
  id,
  companyTypes,
  togglePopin,
  company,
  onUserSaved,
}: any) {
  const token = localStorage.getItem("access_token");
  const { JModal, openModal, setOpenModal, isOpenModal } = useModal();
  const { isAgency, isContributor, isClient } = useCompany();
  const { data: activities = [] } = useActivitiesQuery();

  const companyActivities = activities?.filter((a) =>
    company?.activities?.some((a2) => a2._id === a._id)
  );

  const { isAdmin } = useAuth(); // will be remove with acl

  const { onSubmit, onSubmitEdit, error: errorUser } = useUserForm(id);

  const handleDeleteUser = useCallback(async () => {
    await DeleteUser(token, user?._id);
    setOpenModal(false);
    togglePopin();
  }, [setOpenModal, togglePopin, token, user?._id]);

  // Patch temporaire
  const handleSubmit = async (arg) => {
    const res = user ? await onSubmitEdit(arg) : await onSubmit(arg);
    if (res && !res?.error) {
      onUserSaved?.(res);
    }
    togglePopin();
  };

  return (
    <>
      <FormHeader
        title={title}
        icon={user && isAdmin() ? <TrashLineIcon /> : undefined}
        onClick={openModal}
      />
      <UserForm
        user={user}
        onSubmit={handleSubmit}
        onReset={togglePopin}
        isCoordinator={false}
        canHaveTwoPhones={isAgency(companyTypes)}
        canBeUser={isClient(companyTypes) || isContributor(companyTypes)}
        error={errorUser}
        activitiesOptions={
          isAgency(companyTypes) ? getActivitiesOptions(companyActivities) : []
        }
      />
      {/* this should be in the Form directly since it's linked to it and dependant */}
      {isOpenModal && (
        <JModal
          message={`Êtes vous sur de vouloir supprimer l'utilisateur : ${user?.lastname} ${user?.firstname} (${user?.email})`}
          labelOk="Supprimer"
          labelCancel="Annuler"
          onConfirm={handleDeleteUser}
          onClose={() => setOpenModal(false)}
        />
      )}
    </>
  );
}

type Props = {
  company: any;
  companyTypes: CompanyType[];
  cantEditCurrentTab: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  userList: any[];
  id: string;
};

const FullWidthInfoContainer = styled(InfoContainer)({
  width: "100%",
  marginTop: theme.spacing(2),
  "& .MuiButton-root": {
    color: "#000000 !important",
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5 !important",
      color: "#000000 !important",
      boxShadow: "none !important",
    },
    "&:disabled": {
      color: "#cccccc !important",
      backgroundColor: "transparent !important",
    },
  },
});

const NoPaddingTable = styled(Table)({
  padding: "0 !important",
});

const AddContactButton = styled(SimpleMuiButton)({
  color: "#000000 !important",
  backgroundColor: "transparent !important",
  boxShadow: "none !important",
  border: "none !important",
  transition: "background-color 0.2s ease !important",
  "&:hover": {
    backgroundColor: "#f5f5f5 !important",
    color: "#000000 !important",
    boxShadow: "none !important",
  },
  "&:disabled": {
    color: "#cccccc !important",
    backgroundColor: "transparent !important",
    pointerEvents: "none",
  },
  "&:focus": {
    backgroundColor: "#f5f5f5 !important",
    outline: "none",
  },
});

export const Contacts = memo(function Contacts({
  company,
  companyTypes,
  cantEditCurrentTab,
  isDisabled,
  isLoading,
  userList,
  id,
}: Props) {
  const { Popin, togglePopin, PopinProps } = usePopin();
  const [localUsers, setLocalUsers] = useState<any[]>(userList || []);

  useEffect(() => {
    setLocalUsers(userList || []);
  }, [userList]);

  const { data: activities = [] } = useActivitiesQuery();
  const { isAgency, isCarrier, isContributor, isClient, isLogistician } =
    useCompany();
  const { user, setUser, setError, title, setTitle } = useUserForm(id);

  const handlePopin = useCallback(
    (isOpen) => {
      if (!isOpen) {
        setUser(null);
        setError("");
      }
      togglePopin();
    },
    [setError, setUser, togglePopin]
  );

  const onAddClick = () => {
    setUser(null);
    handlePopin(true);
  };

  const onEditClick = useCallback(
    (_user: User) => {
      if (_user) {
        setUser(_user);
        handlePopin(true);
      }
    },
    [handlePopin, setUser]
  );

  const handleAddUserClick = () => {
    if (cantEditCurrentTab && company?.isActive) {
      setTitle("Ajouter un contact");
      onAddClick();
    }
  };

  const handleEditUserClick = useCallback(
    (data: any) => {
      if (cantEditCurrentTab) {
        setTitle("Édition du contact");
        onEditClick(data);
      }
    },
    [cantEditCurrentTab, onEditClick, setTitle]
  );

  const isActivityDisplayed = isAgency(company?.companyTypes);

  const renderCell = useCallback(
    (user: any, value: ReactNode) => ({
      value,
      isDisable: user.isDisable,
      onClickAction: user.isDisable
        ? undefined
        : () => handleEditUserClick(user),
    }),
    [handleEditUserClick]
  );

  const tableHead = useMemo(
    () => [
      ...(isActivityDisplayed ? [{ id: "activity", label: "activité" }] : []),
      { id: "contact", label: "contact" },
      { id: "email", label: "e-mail" },
      { id: "phone", label: "tél. mobile" },
      { id: "isUser", label: "utilisateur" },
      { id: "isApprover", label: "approbateur" },
    ],
    [isActivityDisplayed]
  );

  const tableBody = useMemo(
    () =>
      localUsers && localUsers.length
        ? localUsers.map((u) => [
            ...(isActivityDisplayed
              ? [
                  renderCell(
                    u,
                    <Text>
                      {
                        ACTIVITIES_LABELS[
                          activities.find((a) => a._id === u.activity)?.name
                        ]
                      }
                    </Text>
                  ),
                ]
              : []),
            renderCell(
              u,
              <Text type="important">
                {u.firstname} {u.lastname?.toUpperCase()}
              </Text>
            ),
            renderCell(u, <Text>{u.email}</Text>),
            renderCell(u, <Text>{u.phone || "N/A"}</Text>),
            renderCell(u, <Text>{u.isOnlyContact ? "Non" : "Oui"}</Text>),
            renderCell(u, <Text>{u.isApprover ? "Oui" : "Non"}</Text>),
          ])
        : [],
    [activities, isActivityDisplayed, localUsers, renderCell]
  );

  return (
    <>
      <FullWidthInfoContainer
        isToggleable
        leftHeader={
          <TitleInfoContainer title="Contact(s)" icon={<UserLineIcon />} />
        }
        rightHeader={
          <AddContactButton
            disabled={isDisabled}
            onClick={handleAddUserClick}
            className=""
            sx={
              company?.isActive === false
                ? {
                    color: "rgba(0, 0, 0, 0.37) !important",
                    pointerEvents: "none",
                  }
                : undefined
            }
            icon={<AddIcon />}
          >
            Ajouter un contact
          </AddContactButton>
        }
      >
        <NoPaddingTable
          bodyMissingLabelText={`${
            isClient(companyTypes)
              ? "Ce client"
              : isContributor(companyTypes)
                ? "Cette entreprise intervenante"
                : isCarrier(companyTypes)
                  ? "Ce transporteur"
                  : isLogistician(companyTypes)
                    ? "Ce logisticien"
                    : "Cette entreprise"
          } n'a pas encore de contacts`}
          load={isLoading}
          rowsPerPage={8}
          thead={tableHead}
          bodyData={tableBody}
          usePaper={false}
        />
      </FullWidthInfoContainer>

      <Popin {...PopinProps}>
        <TestForm
          user={user}
          title={title}
          id={id}
          company={company}
          companyTypes={companyTypes}
          togglePopin={togglePopin}
          onUserSaved={(savedUser: any) => {
            setLocalUsers((prev) => {
              const idx = prev.findIndex((u: any) => u?._id === savedUser?._id);
              if (idx === -1) {
                return [savedUser, ...prev];
              }
              const next = [...prev];
              next[idx] = { ...next[idx], ...savedUser };
              return next;
            });
          }}
        />
      </Popin>
    </>
  );
});
